import {
  CREATOR_SANCTION_MESSAGE,
  isCreatorSanctioned,
} from "@/utils/creatorSanction";
import { useOpenCreatorChat } from "@/hooks/useOpenCreatorChat";
import { useMutation, useQuery } from "@apollo/client/react";
import ModalProvider from "@repo/components/src/globalProviders/modal";
import LoadingButton from "@repo/components/src/shared/loadingButton";
import { Box } from "@repo/components/src/ui/box";
import { Button, ButtonIcon, ButtonText } from "@repo/components/src/ui/button";
import { Icon } from "@repo/components/src/ui/icon";
import { Menu, MenuItem, MenuItemLabel } from "@repo/components/src/ui/menu";
import { SkeletonText } from "@repo/components/src/ui/skeleton";
import { Spinner } from "@repo/components/src/ui/spinner";
import { Text } from "@repo/components/src/ui/text";
import { BulkAddToFavoritesDocument } from "@repo/graphql/documents/brand/mutation.generated";
import { GetBrandAbilitiesDocument } from "@repo/graphql/documents/brand/query.generated";
import { AddToPipelineDocument } from "@repo/graphql/documents/campaign/mutation.generated";
import { AddToPipelineCampaignsDocument } from "@repo/graphql/documents/campaign/query.generated";
import {
  GetUserProfileQuery,
  GetUserProfileQueryVariables,
} from "@repo/graphql/documents/user/query.generated";
import { SubscriptionAbilities } from "@repo/graphql/gql/graphql";
import {
  Tooltip,
  TooltipContent,
  TooltipText,
} from "@repo/components/src/ui/tooltip";
import { CheckIcon, ChevronDown } from "lucide-react-native";
import React, { useState } from "react";

// A menu item's label wrapped in a Tooltip explaining why the (disabled) action
// is blocked. The Menu builds its collection from direct MenuItem children, so
// we cannot wrap the MenuItem itself — a disabled MenuItem still emits hover
// events, so the tooltip triggers on the inner label element instead.
const SanctionLabel = (props: React.PropsWithChildren) => (
  <Tooltip
    placement="left"
    trigger={(tProps) => (
      <Box {...tProps} className="flex-1">
        <MenuItemLabel size="sm">{props.children}</MenuItemLabel>
      </Box>
    )}
  >
    <TooltipContent className="max-w-72">
      <TooltipText size="sm">{CREATOR_SANCTION_MESSAGE}</TooltipText>
    </TooltipContent>
  </Tooltip>
);

type Props = {
  brandId: string;
  campaignId?: string;
  userProfileQuery: useQuery.Result<
    GetUserProfileQuery,
    GetUserProfileQueryVariables
  >;
};

const ProfileDrawerActions = React.memo((props: Props) => {
  const user = props.userProfileQuery.data?.userResource.get;
  const { data: brandAbilities, loading: brandAbilityLoading } = useQuery(
    GetBrandAbilitiesDocument,
    {
      variables: {
        id: props.brandId,
      },
    },
  );
  const { data: campaignData, loading: campaignLoading } = useQuery(
    AddToPipelineCampaignsDocument,
    {
      variables: {
        brand_Id: props.brandId,
        acceptingInvitations: true,
        first: 100,
        orderBy: "-id",
        userPreselectionId: user?.id,
      },
      skip: !user,
    },
  );
  const canAddChat = brandAbilities?.brand.get?.subscriptionAbilities.find(
    (ability) => ability.ability === SubscriptionAbilities.Chats,
  )?.can;
  const [menuOpen, setMenuOpen] = useState(false);
  const [addToFavorite, { loading: addToFavoriteLoading }] = useMutation(
    BulkAddToFavoritesDocument,
  );
  const [addToPipeline, { loading: addToPipelineLoading }] = useMutation(
    AddToPipelineDocument,
  );
  const { openChat, loading: addChatLoading } = useOpenCreatorChat();
  const isInPreselection =
    props.userProfileQuery.data?.userResource.get.isInPreselection;
  const isInBrandFavorite =
    props.userProfileQuery.data?.userResource.get.isInBrandFavorite;
  const isSanctioned = isCreatorSanctioned(user?.creatorSanction?.status);
  return (
    <Menu
      offset={8}
      isOpen={menuOpen}
      onOpen={() => setMenuOpen(true)}
      onClose={() => setMenuOpen(false)}
      placement="bottom right"
      closeOnSelect={false}
      className="gap-y-1 min-w-60"
      trigger={({ ...triggerProps }) => {
        return (
          <LoadingButton
            {...triggerProps}
            size="xs"
            variant="solid"
            className="gap-x-2"
            action="brand"
            rounded="lg"
            loading={props.userProfileQuery.loading || brandAbilityLoading}
          >
            <ButtonText>Gestionar</ButtonText>
            <ButtonIcon as={ChevronDown} />
          </LoadingButton>
        );
      }}
    >
      {props.campaignId ? (
        <MenuItem
          key="dowload-all"
          className={`border rounded-lg ${isInPreselection ? "border-brand-400 bg-background-50" : "border-transparent"}`}
          disabled={isInPreselection || addToPipelineLoading || isSanctioned}
          onPress={() => {
            props.campaignId &&
              user &&
              addToPipeline({
                variables: {
                  campaignId: props.campaignId,
                  userIds: [user.id],
                  orderBy: "user_FirstName,user_LastName",
                },
              });
          }}
        >
          {isSanctioned ? (
            <SanctionLabel>
              {isInPreselection ? "Pre-seleccionado" : "Pre-seleccionar"}
            </SanctionLabel>
          ) : (
            <MenuItemLabel size="sm" className="flex-1">
              {isInPreselection ? "Pre-seleccionado" : "Pre-seleccionar"}
            </MenuItemLabel>
          )}
          <Icon as={CheckIcon} hidden={!isInPreselection} action="brand" />
        </MenuItem>
      ) : isSanctioned ? (
        <MenuItem key="add-to-pipeline" disabled>
          <SanctionLabel>Preseleccionar</SanctionLabel>
        </MenuItem>
      ) : (
        <MenuItem
          key="add-to-pipeline"
          className="group"
          disabled={addToPipelineLoading}
        >
          <MenuItemLabel size="sm" className="flex-1">
            Preseleccionar
          </MenuItemLabel>
          {addToPipelineLoading && <Spinner />}
          <Box className="hidden group-hover:block absolute right-full bg-background-0 rounded-lg px-2 py-2 shadow-lg w-96 gap-y-2 overflow-y-scroll overflow-x-hidden max-h-[75vh] top-0">
            <SkeletonText
              _lines={3}
              className="rounded-lg h-8"
              isLoaded={!campaignLoading}
            >
              {campaignData?.campaign.list.edges.length === 0 ? (
                <Box className="p-2">
                  <Text>No hay campañas disponibles.</Text>
                </Box>
              ) : (
                campaignData?.campaign.list.edges.map((edge) => {
                  const campaign = edge?.node;
                  return (
                    <Button
                      key={campaign.id}
                      className={`w-full justify-start gap-x-2 border ${edge.node.hasUserPreselection ? "bg-background-50 border-brand-400" : ""}`}
                      size="sm"
                      rounded="lg"
                      action="default"
                      isDisabled={edge.node.hasUserPreselection}
                      onPress={() => {
                        !edge.node.hasUserPreselection &&
                          user &&
                          addToPipeline({
                            variables: {
                              campaignId: campaign.id,
                              userIds: [user.id],
                            },
                          });
                      }}
                    >
                      <ButtonText className="flex-1" isTruncated>
                        {campaign.title}
                      </ButtonText>
                      <ButtonIcon
                        as={CheckIcon}
                        hidden={!edge.node.hasUserPreselection}
                        className="text-brand-400"
                      />
                    </Button>
                  );
                })
              )}
            </SkeletonText>
          </Box>
        </MenuItem>
      )}
      <MenuItem
        key="dowload-filtered"
        className={`border rounded-lg ${isInBrandFavorite ? "border-brand-400 bg-background-50" : "border-transparent"}`}
        onPress={() => {
          user &&
            addToFavorite({
              variables: {
                favorites: [{ brandId: props.brandId, userId: user.id }],
                brandId: props.brandId,
              },
            });
        }}
        disabled={addToFavoriteLoading || isInBrandFavorite || isSanctioned}
      >
        {isSanctioned ? (
          <SanctionLabel>
            {isInBrandFavorite ? "Agregado a favoritos" : "Agregar a favoritos"}
          </SanctionLabel>
        ) : (
          <MenuItemLabel size="sm" className="flex-1">
            {isInBrandFavorite ? "Agregado a favoritos" : "Agregar a favoritos"}
          </MenuItemLabel>
        )}
        <Icon as={CheckIcon} hidden={!isInBrandFavorite} action="brand" />
        {addToFavoriteLoading && <Spinner />}
      </MenuItem>
      {canAddChat && (
        <MenuItem
          key="chat"
          onPress={() => {
            user &&
              openChat({
                brandId: props.brandId,
                userId: user.id,
                onOpened: () => {
                  setMenuOpen(false);
                  ModalProvider.hide();
                },
              });
          }}
          disabled={addChatLoading}
        >
          <MenuItemLabel size="sm" className="flex-1">
            Enviar mensaje
          </MenuItemLabel>
          {addChatLoading && <Spinner />}
        </MenuItem>
      )}
    </Menu>
  );
});

ProfileDrawerActions.displayName = "ProfileDrawerActions";

export default ProfileDrawerActions;
